import React, { useContext } from 'react';
import styled from 'styled-components';

import { RadioButton, RadioGroup, Switch, Title, Tooltip } from 'components';
import { StyledRadioWrapper } from 'components/Radio/StyledComponents';
import { CALENDAR_OPTIONS } from 'utils';
import { useBreakpoint } from 'hooks';
import { StyledSwitchGrid } from '../components';
import AgentUserCalendarContext from '../../ShowingsCalendarPage/AgentUserCalendarContext';
import FormContext from '../FormContext';

const AgentUserCalendarSync = () => {
  const {
    isAuthenticated,
    agentUserCalendarSubscriptions,
    setAgentUserCalendarSubscriptions,
    calendarProvider,
    setCalendarProvider,
    existingCalendarProvider,
  } = useContext(AgentUserCalendarContext);

  const { setWorking } = useContext(FormContext);

  const {
    buyerShowingCalendarSubscription,
    listingShowingCalendarSubscription,
    showBuyerShowingsAsBusy,
    showListingShowingsAsBusy,
  } = agentUserCalendarSubscriptions || {};

  const { md } = useBreakpoint();

  const DESYNC_CALENDAR_OPTION = {
    serviceName: 'none',
    title: 'None',
    subtitle: '',
    altText: 'disconnect calendar',
    image: undefined as string,
  } as const;

  const CalendarOptionsWrapper = ({
    children,
  }: {
    children: React.ReactElement;
  }) => {
    if (children.key === existingCalendarProvider) return children;
    if (!existingCalendarProvider || existingCalendarProvider === 'none') {
      return children;
    }

    return (
      <Tooltip
        title={
          'This will disconnect BrokerBay from your current calendar service.'
        }
        overlayStyle={{ maxWidth: md && '500px' }}
      >
        {children}
      </Tooltip>
    );
  };

  return (
    <>
      <Title level={3}>Calendar Sync</Title>

      <StyledSwitchGrid>
        <Switch
          checked={buyerShowingCalendarSubscription}
          onChange={checked => {
            if (!isAuthenticated) {
              setWorking(checked || listingShowingCalendarSubscription);
            }
            setAgentUserCalendarSubscriptions(cur => ({
              ...cur,
              buyerShowingCalendarSubscription: checked,
            }));
          }}
        />{' '}
        Automatically sync pending and confirmed buyer showings
        <Switch
          checked={listingShowingCalendarSubscription}
          onChange={checked => {
            if (!isAuthenticated) {
              setWorking(checked || buyerShowingCalendarSubscription);
            }
            setAgentUserCalendarSubscriptions(cur => ({
              ...cur,
              listingShowingCalendarSubscription: checked,
            }));
          }}
        />{' '}
        Automatically sync pending and confirmed listing showings
        <Switch
          checked={showBuyerShowingsAsBusy}
          onChange={checked => {
            if (!isAuthenticated) {
              setWorking(checked || showBuyerShowingsAsBusy);
            }
            setAgentUserCalendarSubscriptions(curr => ({
              ...curr,
              showBuyerShowingsAsBusy: checked,
            }));
          }}
        />{' '}
        Automatically set synced buyer showings as busy in my calendar
        <Switch
          checked={showListingShowingsAsBusy}
          onChange={checked => {
            if (!isAuthenticated) {
              setWorking(checked || showListingShowingsAsBusy);
            }
            setAgentUserCalendarSubscriptions(curr => ({
              ...curr,
              showListingShowingsAsBusy: checked,
            }));
          }}
        />{' '}
        Automatically set synced listing showings as busy in my calendar
      </StyledSwitchGrid>
      {(buyerShowingCalendarSubscription ||
        listingShowingCalendarSubscription) && (
        <StyledFullWidthRadioGroup
          value={calendarProvider}
          label="Select a calendar provider:"
        >
          {[...[DESYNC_CALENDAR_OPTION], ...CALENDAR_OPTIONS].map(option => (
            <CalendarOptionsWrapper key={option.serviceName}>
              <StyledRadioButton
                key={option.serviceName}
                value={option.serviceName}
                onClick={() => {
                  setCalendarProvider(option.serviceName);
                  setWorking(!isAuthenticated && option.serviceName === 'none');
                }}
                image={!!option.image}
              >
                <StyledCalenderProviderContainer>
                  {option.image && (
                    <img src={option.image} alt={option.altText} width="25" />
                  )}
                  {option.title}
                </StyledCalenderProviderContainer>
              </StyledRadioButton>
            </CalendarOptionsWrapper>
          ))}
        </StyledFullWidthRadioGroup>
      )}
    </>
  );
};

export default AgentUserCalendarSync;

const StyledRadioButton = styled(RadioButton)<{ image?: boolean }>`
  ${({ image }) =>
    !image && 'display: flex; align-items: center; padding-bottom: 8px;'}
`;

const StyledCalenderProviderContainer = styled.div`
  display: flex;
  flex-flow: column;
  justify-content: center;
  align-items: center;
  width: 80px;
`;

const StyledFullWidthRadioGroup = styled(RadioGroup)`
  ${StyledRadioWrapper} {
    width: auto;
  }
  & .ant-radio-group {
    width: 100%;
    display: flex;
  }
  & .ant-radio-button-wrapper {
    display: flex;
    justify-content: center;
    padding-top: 8px;
    height: auto;
    flex: 1;
  }
`;
